import React, { useState, useEffect } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Modal, Button, Input, Table, TableHead, TableHeader, TableBody, TableRow, TableCell, Badge } from '../design/primitives';
import { Spinner } from './ui';
import { apiClient } from '../api/apiConfig';
import { NoSymbolIcon, TrashIcon } from '@heroicons/react/24/outline';

interface BlockedSender {
  id: number;
  value: string;
  block_type: 'email' | 'domain';
  reason: string;
  created_at: string;
}

interface BlockedSenderManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const BlockedSenderManager: React.FC<BlockedSenderManagerProps> = ({ isOpen, onClose }) => {
  const [blockedSenders, setBlockedSenders] = useState<BlockedSender[]>([]);
  const [loading, setLoading] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newBlockType, setNewBlockType] = useState<'email' | 'domain'>('email');

  const fetchBlockedSenders = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/blocked-senders/');
      setBlockedSenders(res.data?.results || res.data || []);
    } catch (err) {
      console.error('Failed to fetch blocked senders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchBlockedSenders();
  }, [isOpen]);

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    try {
      await apiClient.post('/api/blocked-senders/', {
        value: newValue.trim().toLowerCase(),
        block_type: newBlockType,
        reason: 'Manually blocked',
      });
      setNewValue('');
      fetchBlockedSenders();
    } catch (err: any) {
      console.error('Failed to add blocked sender:', err);
    }
  };

  const handleUnblock = async (id: number) => {
    try {
      await apiClient.post(`/api/blocked-senders/${id}/unblock/`);
      setBlockedSenders(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Failed to unblock:', err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Blocked Senders"
      footer={
        <Button variant="secondary" onClick={onClose}>Close</Button>
      }
    >
      {/* Add new block */}
      <div style={{ display: 'flex', gap: SPACING[8], marginBottom: SPACING[16] }}>
        <div style={{ display: 'flex', gap: SPACING[4] }}>
          <button
            onClick={() => setNewBlockType('email')}
            style={{
              padding: `${SPACING[4]} ${SPACING[8]}`,
              fontSize: TYPOGRAPHY.body.sizes.small,
              border: `1px solid ${newBlockType === 'email' ? COLORS.accent.default : COLORS.border.default}`,
              borderRadius: RADIUS,
              backgroundColor: newBlockType === 'email' ? COLORS.accent.default : 'transparent',
              color: newBlockType === 'email' ? '#fff' : COLORS.text.secondary,
              cursor: 'pointer',
            }}
          >
            Email
          </button>
          <button
            onClick={() => setNewBlockType('domain')}
            style={{
              padding: `${SPACING[4]} ${SPACING[8]}`,
              fontSize: TYPOGRAPHY.body.sizes.small,
              border: `1px solid ${newBlockType === 'domain' ? COLORS.accent.default : COLORS.border.default}`,
              borderRadius: RADIUS,
              backgroundColor: newBlockType === 'domain' ? COLORS.accent.default : 'transparent',
              color: newBlockType === 'domain' ? '#fff' : COLORS.text.secondary,
              cursor: 'pointer',
            }}
          >
            Domain
          </button>
        </div>
        <div style={{ flex: 1 }}>
          <Input
            placeholder={newBlockType === 'email' ? 'spam@example.com' : 'example.com'}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
        </div>
        <Button variant="primary" onClick={handleAdd} icon={<NoSymbolIcon style={{ width: '14px', height: '14px' }} />}>
          Block
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: SPACING[24] }}><Spinner /></div>
      ) : blockedSenders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: SPACING[24], color: COLORS.text.tertiary }}>
          No blocked senders yet
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Value</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Reason</TableHeader>
              <TableHeader align="right">Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedSenders.map(b => (
              <TableRow key={b.id}>
                <TableCell>
                  <span style={{ fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                    {b.value}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={b.block_type === 'domain' ? 'warning' : 'default'}>
                    {b.block_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.small }}>
                    {b.reason || '-'}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="ghost"
                    onClick={() => handleUnblock(b.id)}
                    icon={<TrashIcon style={{ width: '14px', height: '14px' }} />}
                    style={{ color: COLORS.status.error }}
                  >
                    Unblock
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Modal>
  );
};

export default BlockedSenderManager;
